import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const JobSeekerKYC = ({ step, formData, updateFormData, onNext, onBack, loading }) => {
  const [profilePreview, setProfilePreview] = useState(null);
  const [submitError, setSubmitError] = useState('');

  // Document upload handlers
  const handleFileUpload = (event, documentType, index = null) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = {
      cv: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      certificates: ['application/pdf', 'image/jpeg', 'image/png'],
      portfolio: ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'],
      profilePhoto: ['image/jpeg', 'image/png', 'image/gif']
    };

    if (!allowedTypes[documentType].includes(file.type)) {
      alert(`Invalid file type for ${documentType}. Please upload a valid file.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const currentDocuments = { ...formik.values.documents };
      
      if (documentType === 'cv' || documentType === 'profilePhoto') {
        currentDocuments[documentType] = {
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: e.target.result
        };
        
        // Update preview state for profile photo only
        if (documentType === 'profilePhoto') {
          setProfilePreview(e.target.result);
        }
      } else {
        // For arrays (certificates, portfolio)
        if (index !== null) {
          currentDocuments[documentType][index] = {
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            preview: e.target.result
          };
        } else {
          currentDocuments[documentType].push({
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            preview: e.target.result
          });
        }
        
        // Previews are stored in formik documents; no extra state needed
      }
      
      formik.setFieldValue('documents', currentDocuments);
    };
    
    reader.readAsDataURL(file);
  };

  const removeDocument = (documentType, index = null) => {
    const currentDocuments = { ...formik.values.documents };
    
    if (documentType === 'cv' || documentType === 'profilePhoto') {
      currentDocuments[documentType] = null;
      if (documentType === 'profilePhoto') {
        setProfilePreview(null);
      }
    } else {
      currentDocuments[documentType].splice(index, 1);
    }
    
    formik.setFieldValue('documents', currentDocuments);
  };

  const addDocumentSlot = (documentType) => {
    const currentDocuments = { ...formik.values.documents };
    currentDocuments[documentType].push(null);
    formik.setFieldValue('documents', currentDocuments);
  };

  // Predefined options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  const employmentTypeOptions = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
  const locationOptions = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Machakos'];

  // Validation schema
  const getValidationSchema = () => {
    const baseSchema = {
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      dateOfBirth: Yup.date().required('Date of birth is required'),
      gender: Yup.string().required('Gender is required'),
      nationalId: Yup.string().required('National ID is required'),
      address: Yup.string().required('Address is required'),
    };

    if (step === 'education') {
      baseSchema.education = Yup.array().of(
        Yup.object().shape({
          institution: Yup.string().required('Institution is required'),
          degree: Yup.string().required('Degree is required'),
          fieldOfStudy: Yup.string().required('Field of study is required'),
          startYear: Yup.string().required('Start year is required'),
          endYear: Yup.string().required('End year is required')
        })
      );
    }

    if (step === 'experience') {
      baseSchema.workExperience = Yup.array().of(
        Yup.object().shape({
          company: Yup.string().required('Company is required'),
          position: Yup.string().required('Position is required'),
          startDate: Yup.date().required('Start date is required'),
          endDate: Yup.date().nullable(),
          description: Yup.string().required('Job description is required')
        })
      );
    }

    if (step === 'skills') {
      baseSchema.skills = Yup.array().min(1, 'At least one skill is required');
      baseSchema.desiredJobRole = Yup.string().required('Desired job role is required');
    }

    if (step === 'preferences') {
      baseSchema.employmentType = Yup.array().min(1, 'At least one employment type is required');
      baseSchema.expectedSalary = Yup.object().shape({
        min: Yup.number().min(0, 'Minimum salary must be positive'),
        max: Yup.number().min(0, 'Maximum salary must be positive')
      });
      baseSchema.preferredLocations = Yup.array().min(1, 'At least one location is required');
    }

    return Yup.object().shape(baseSchema);
  };

  const formik = useFormik({
    initialValues: {
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      dateOfBirth: formData.dateOfBirth || '',
      gender: formData.gender || '',
      nationalId: formData.nationalId || '',
      address: formData.address || '',
      education: formData.education || [{
        institution: '',
        degree: '',
        fieldOfStudy: '',
        grade: '',
        startYear: '',
        endYear: ''
      }],
      workExperience: formData.workExperience || [{
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        description: ''
      }],
      skills: formData.skills || [],
      skillsInput: formData.skillsInput || '',
      desiredJobRole: formData.desiredJobRole || '',
      employmentType: formData.employmentType || [],
      expectedSalary: formData.expectedSalary || { min: 3000, max: 15000 },
      preferredLocations: formData.preferredLocations || [],
      // Document uploads
      documents: formData.documents || {
        cv: null,
        certificates: [],
        portfolio: [],
        profilePhoto: null
      }
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      setSubmitError('');
      updateFormData(values);
      onNext();
    }
  });

  const handleNext = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors || {}).length > 0) {
      const touched = {};
      Object.keys(errors).forEach((k) => { touched[k] = true; });
      formik.setTouched(touched, true);
      const firstErrKey = Object.keys(errors)[0];
      const firstErrMsg = typeof errors[firstErrKey] === 'string' ? errors[firstErrKey] : 'Please complete the required fields';
      setSubmitError(firstErrMsg);
      return;
    }
    formik.handleSubmit();
  };

  const getStepTitle = () => {
    switch (step) {
      case 'personal':
        return 'Personal Information';
      case 'education':
        return 'Education Background';
      case 'experience':
        return 'Work Experience';
      case 'skills':
        return 'Skills & Expertise';
      case 'preferences':
        return 'Job Preferences';
      default:
        return 'Profile Setup';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'personal':
        return 'Tell us about yourself';
      case 'education':
        return 'Share your educational background';
      case 'experience':
        return 'Add your work experience';
      case 'skills':
        return 'Highlight your skills and expertise';
      case 'preferences':
        return 'Set your job preferences and upload documents';
      default:
        return 'Complete your profile';
    }
  };

  // Document upload section component
  const renderDocumentUpload = () => (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Document Uploads
      </Typography>
      
      {/* Profile Photo */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Profile Photo (Optional)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-photo-upload-doc"
              type="file"
              onChange={(e) => handleFileUpload(e, 'profilePhoto')}
            />
            <label htmlFor="profile-photo-upload-doc">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
              >
                Upload Photo
              </Button>
            </label>
            {formik.values.documents.profilePhoto && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={formik.values.documents.profilePhoto.preview}
                  sx={{ width: 40, height: 40 }}
                />
                <Typography variant="body2">
                  {formik.values.documents.profilePhoto.name}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeDocument('profilePhoto')}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    
      {/* CV Upload */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            CV/Resume (Required)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="cv-upload-doc"
              type="file"
              onChange={(e) => handleFileUpload(e, 'cv')}
            />
            <label htmlFor="cv-upload-doc">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
              >
                Upload CV
              </Button>
            </label>
            {formik.values.documents.cv && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {formik.values.documents.cv.name}
                </Typography>
                <Chip 
                  label={`${(formik.values.documents.cv.size / 1024 / 1024).toFixed(2)} MB`} 
                  size="small" 
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeDocument('cv')}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Accepted formats: PDF, DOC, DOCX (Max 5MB)
          </Typography>
        </CardContent>
      </Card>
    
      {/* Certificates */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Certificates & Qualifications (Optional)
          </Typography>
          {formik.values.documents.certificates.map((cert, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {cert ? (
                <>
                  <Typography variant="body2">{cert.name}</Typography>
                  <Chip 
                    label={`${(cert.size / 1024 / 1024).toFixed(2)} MB`} 
                    size="small" 
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeDocument('certificates', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <input
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    id={`certificate-upload-${index}`}
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'certificates', index)}
                  />
                  <label htmlFor={`certificate-upload-${index}`}>
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Upload Certificate
                    </Button>
                  </label>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeDocument('certificates', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              )}
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => addDocumentSlot('certificates')}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Certificate
          </Button>
          <Typography variant="caption" color="text.secondary" display="block">
            Accepted formats: PDF, JPG, PNG (Max 5MB each)
          </Typography>
        </CardContent>
      </Card>
    
      {/* Portfolio */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Portfolio/Work Samples (Optional)
          </Typography>
          {formik.values.documents.portfolio.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {item ? (
                <>
                  <Typography variant="body2">{item.name}</Typography>
                  <Chip 
                    label={`${(item.size / 1024 / 1024).toFixed(2)} MB`} 
                    size="small" 
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeDocument('portfolio', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <input
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    style={{ display: 'none' }}
                    id={`portfolio-upload-${index}`}
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'portfolio', index)}
                  />
                  <label htmlFor={`portfolio-upload-${index}`}>
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Upload Portfolio Item
                    </Button>
                  </label>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeDocument('portfolio', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              )}
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => addDocumentSlot('portfolio')}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Portfolio Item
          </Button>
          <Typography variant="caption" color="text.secondary" display="block">
            Accepted formats: PDF, JPG, PNG, GIF (Max 5MB each)
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStepContent = () => {
    switch (step) {
      case 'personal':
        return (
          <Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                 <TextField
                   fullWidth
                   label="Date of Birth"
                   name="dateOfBirth"
                   type="date"
                   InputLabelProps={{ shrink: true }}
                   value={formik.values.dateOfBirth}
                   onChange={formik.handleChange}
                   error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                   helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                 />
               </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    error={formik.touched.gender && Boolean(formik.errors.gender)}
                  >
                    {genderOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="National ID"
                  name="nationalId"
                  value={formik.values.nationalId}
                  onChange={formik.handleChange}
                  error={formik.touched.nationalId && Boolean(formik.errors.nationalId)}
                  helperText={formik.touched.nationalId && formik.errors.nationalId}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={3}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-photo-upload"
                type="file"
                onChange={(e) => handleFileUpload(e, 'profilePhoto')}
              />
              <label htmlFor="profile-photo-upload">
                <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                  Upload Profile Photo
                </Button>
              </label>
              {profilePreview && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Profile photo uploaded successfully!
                </Alert>
              )}
            </Box>
          </Box>
        );

      case 'preferences':
        return (
          <Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    multiple
                    name="employmentType"
                    value={formik.values.employmentType}
                    onChange={formik.handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {employmentTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expected Salary (Min)"
                  name="expectedSalary.min"
                  type="number"
                  value={formik.values.expectedSalary.min}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expected Salary (Max)"
                  name="expectedSalary.max"
                  type="number"
                  value={formik.values.expectedSalary.max}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Work Locations</InputLabel>
                  <Select
                    multiple
                    name="preferredLocations"
                    value={formik.values.preferredLocations}
                    onChange={formik.handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {locationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Document Upload Section */}
            {renderDocumentUpload()}
          </Box>
        );

      case 'education':
        return (
          <Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Education History
                </Typography>
                {formik.values.education.map((edu, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Institution Name"
                            name={`education[${index}].institution`}
                            value={edu.institution}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Degree/Qualification"
                            name={`education[${index}].degree`}
                            value={edu.degree}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Field of Study"
                            name={`education[${index}].fieldOfStudy`}
                            value={edu.fieldOfStudy}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Grade/GPA"
                            name={`education[${index}].grade`}
                            value={edu.grade}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Start Year"
                            name={`education[${index}].startYear`}
                            type="number"
                            value={edu.startYear}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="End Year"
                            name={`education[${index}].endYear`}
                            type="number"
                            value={edu.endYear}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const newEducation = formik.values.education.filter((_, i) => i !== index);
                              formik.setFieldValue('education', newEducation);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newEducation = [...formik.values.education, {
                      institution: '',
                      degree: '',
                      fieldOfStudy: '',
                      grade: '',
                      startYear: '',
                      endYear: ''
                    }];
                    formik.setFieldValue('education', newEducation);
                  }}
                >
                  Add Education
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      case 'experience':
        return (
          <Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Work Experience
                </Typography>
                {formik.values.workExperience.map((exp, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Company Name"
                            name={`workExperience[${index}].company`}
                            value={exp.company}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Job Title"
                            name={`workExperience[${index}].position`}
                            value={exp.position}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Start Date"
                            name={`workExperience[${index}].startDate`}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={exp.startDate}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="End Date"
                            name={`workExperience[${index}].endDate`}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={exp.endDate}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Job Description"
                            name={`workExperience[${index}].description`}
                            multiline
                            rows={3}
                            value={exp.description}
                            onChange={formik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const newExperience = formik.values.workExperience.filter((_, i) => i !== index);
                              formik.setFieldValue('workExperience', newExperience);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newExperience = [...formik.values.workExperience, {
                      company: '',
                      position: '',
                      startDate: '',
                      endDate: '',
                      description: ''
                    }];
                    formik.setFieldValue('workExperience', newExperience);
                  }}
                >
                  Add Experience
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      case 'skills':
        return (
          <Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Skills & Expertise
                </Typography>
                <TextField
                  fullWidth
                  label="Skills (comma-separated)"
                  name="skillsInput"
                  value={formik.values.skillsInput}
                  onChange={(e) => {
                    formik.setFieldValue('skillsInput', e.target.value);
                    const skillsArray = e.target.value.split(',').map(skill => skill.trim()).filter(skill => skill);
                    formik.setFieldValue('skills', skillsArray);
                  }}
                  helperText="Enter your skills separated by commas (e.g., JavaScript, React, Node.js)"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {formik.values.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      onDelete={() => {
                        const newSkills = formik.values.skills.filter((_, i) => i !== index);
                        formik.setFieldValue('skills', newSkills);
                        formik.setFieldValue('skillsInput', newSkills.join(', '));
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Desired Job Role"
                  name="desiredJobRole"
                  value={formik.values.desiredJobRole}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Step content for {step} is not implemented yet.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          {getStepTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getStepDescription()}
        </Typography>
      </Box>

      {renderStepContent()}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          onClick={onBack} 
          disabled={loading}
          variant="outlined"
          size="large"
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading}
          size="large"
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            step === 'preferences' ? 'Next' : 'Continue'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default JobSeekerKYC;